import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Phone, Mail, Download, FileText, X, Loader2, XCircle, Clock, Check } from "lucide-react";
import { BrandMark } from "@/lib/branding";
import { BlockRenderer } from "@/components/proposalBlocks/BlockRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimal PT/EN mapping for client-facing strings on this page.
// Language comes from `qualification.contact.language` (defaults to "pt").
const _I18N = {
  pt: {
    not_found: "Proposta não encontrada",
    accept_toast: "Proposta aceite. Consulte os dados de pagamento abaixo.",
    accept_name_required: "Indique o seu nome completo",
    download_pdf: "Descarregar PDF",
    accept_proposal: "Aceitar Proposta",
    accepted_thanks: "Proposta aceite · Obrigado!",
    decline_or_postpone: "Recusar ou adiar decisão",
    decline_final: "Recusar definitivamente esta proposta.",
    reason_label: "Motivo",
    confirm_decline: "Confirmar recusa",
    send_request: "Enviar pedido",
    preview_note: "PREVIEW DO GESTOR — o cliente ainda não vê esta versão. Clique em Enviar Proposta ao Cliente na página do lead para libertar o link.",
    accept_your_name: "O seu nome completo",
    accept_action_hint: "Ao clicar em Aceitar declara que leu e concorda com a proposta.",
  },
  en: {
    not_found: "Proposal not found",
    accept_toast: "Proposal accepted. Please review the payment details below.",
    accept_name_required: "Please provide your full name",
    download_pdf: "Download PDF",
    accept_proposal: "Accept Proposal",
    accepted_thanks: "Proposal accepted · Thank you!",
    decline_or_postpone: "Decline or postpone",
    decline_final: "Decline this proposal permanently.",
    reason_label: "Reason",
    confirm_decline: "Confirm decline",
    send_request: "Send request",
    preview_note: "MANAGER PREVIEW — the client does not see this version yet. Click Send Proposal on the lead page to release the link.",
    accept_your_name: "Your full name",
    accept_action_hint: "By clicking Accept you confirm that you have read and agree with the proposal.",
  },
};
function _pickLang(d) {
  const l = d?.qualification?.contact?.language || "pt";
  return (String(l).toLowerCase().startsWith("en")) ? "en" : "pt";
}

export default function PublicProposal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [obsText, setObsText] = useState("");
  const [obsName, setObsName] = useState("");
  const [sendingObs, setSendingObs] = useState(false);
  const [obsSubmitted, setObsSubmitted] = useState(false);
  // Decline / postpone dialog
  const [showDecline, setShowDecline] = useState(false);
  const [declineDecision, setDeclineDecision] = useState("postpone");
  const [declineReason, setDeclineReason] = useState("");
  const [declineName, setDeclineName] = useState("");
  const [decliningLoad, setDecliningLoad] = useState(false);
  // i18n helper (PT by default; switches to EN when contact.language = "en")
  const T = _I18N[_pickLang(d)];

  const load = () => {
    // Staff preview: if the current browser has a valid Caring JWT
    // (Gestor logged in), send it as Bearer so the backend can bypass
    // the "revision" freeze. Clients never have a token so they see
    // the frozen page as intended.
    const tk = typeof window !== "undefined" ? window.localStorage.getItem("caring_token") : null;
    const cfg = tk ? { headers: { Authorization: `Bearer ${tk}` } } : {};
    return axios.get(`${BACKEND_URL}/api/public/proposal/${token}`, cfg).then((r) => {
      setD(r.data);
      if (r.data.acceptance?.accepted) setAccepted(true);
      if (r.data.payment) setPaymentInfo(r.data.payment);
      // Pre-populate the decline dialog with prior response so "update decision" works nicely
      if (r.data.decline?.submitted_by_name) setDeclineName(r.data.decline.submitted_by_name);
      if (r.data.decline?.reason) setDeclineReason(r.data.decline.reason);
      if (r.data.decline?.decision) setDeclineDecision(r.data.decline.decision);
    }).catch(() => setError(_I18N[_pickLang(null)].not_found));
  };
  useEffect(() => { load(); }, [token]);

  const accept = async () => {
    if (!name.trim()) { toast.error(T.accept_name_required); return; }
    setAccepting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/proposal/${token}/accept`, { accepted_by_name: name });
      setAccepted(true);
      setPaymentInfo(r.data.payment || null);
      toast.success(T.accept_toast);
      await load();
      // Deixamos o cliente 8s para ler os dados bancários antes de
      // redireccionar para os dados do contrato.
      setTimeout(() => navigate(`/proposal/${token}/contract-data`), 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setAccepting(false); }
  };

  const submitObservations = async () => {
    if (!obsText.trim()) { toast.error("Escreva a sua observação"); return; }
    setSendingObs(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/proposal/${token}/observations`, {
        text: obsText.trim(),
        submitted_by_name: obsName.trim() || null,
      });
      toast.success("Obrigado! A sua observação foi enviada à Gestora.");
      setObsSubmitted(true);
      setObsText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally {
      setSendingObs(false);
    }
  };

  const submitDecline = async () => {
    if (declineDecision === "declined" && !declineReason.trim()) {
      toast.error("Por favor indique brevemente o motivo da recusa.");
      return;
    }
    setDecliningLoad(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/proposal/${token}/decline`, {
        decision: declineDecision,
        reason: declineReason.trim(),
        submitted_by_name: declineName.trim() || null,
      });
      toast.success(declineDecision === "declined"
        ? "Registámos a sua recusa. A Gestora será notificada."
        : "Obrigado. A Gestora contactá-lo-á para agendar novo momento.");
      setShowDecline(false);
      setDeclineReason("");
      setDeclineName("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registar");
    } finally {
      setDecliningLoad(false);
    }
  };

  if (error) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-lg text-center">
        <div className="shift-chip shift-chip-danger mb-6 mx-auto">Proposta indisponível</div>
        <h1 className="font-serif text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">{error}</h1>
        <p className="text-muted-foreground text-lg leading-relaxed">
          Este link pode ter expirado ou já não estar activo. Contacte o consultor Caring com quem falou para receber uma nova ligação.
        </p>
      </div>
    </div>
  );
  if (!d) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="shift-chip mb-6 mx-auto">A preparar</div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">A carregar a proposta…</h1>
        <p className="text-muted-foreground">Um instante enquanto obtemos os detalhes.</p>
      </div>
    </div>
  );

  // Proposta em revisão pelo Gestor (regenerada mas ainda não re-enviada).
  // O cliente NÃO deve ver o conteúdo antigo — apresentamos uma mensagem
  // amigável a explicar que a versão está a ser preparada.
  if (d.revision) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-xl border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-5">
            <BrandMark size={32} />
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center" data-testid="proposal-under-revision">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] mb-6">
            <Clock className="w-8 h-8" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight text-primary mb-4">
            A sua proposta está a ser actualizada
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed mb-6">
            {d.customer_name ? `${d.customer_name}, a` : "A"} sua Gestora
            {d.assigned_manager_name ? <> <b>{d.assigned_manager_name}</b></> : null} está a preparar
            uma versão nova da proposta com base na conversa mais recente.
          </p>
          <p className="text-muted-foreground text-sm">
            Enviaremos o novo link por email assim que estiver pronta.
            Este link deixará de mostrar a versão anterior para evitar confusões.
          </p>
          {d.assigned_manager_name && (
            <p className="text-muted-foreground text-xs mt-8">
              Dúvidas urgentes? Contacte a sua Gestora.
            </p>
          )}
        </main>
      </div>
    );
  }

  const forms = {
    observations: {
      obsText, setObsText, obsName, setObsName,
      sending: sendingObs, submitted: obsSubmitted,
      onSubmit: submitObservations,
      onReset: () => setObsSubmitted(false),
    },
    acceptance: {
      name, setName, accepting,
      onAccept: accept,
      onGoContract: () => navigate(`/proposal/${token}/contract-data`),
    },
  };

  const blocks = d.template_blocks || [];
  const alreadyDeclined = d.decline?.decision === "declined";
  const alreadyPostponed = d.decline?.decision === "postpone";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {d.staff_preview && (
        <div data-testid="staff-preview-banner"
             className="bg-amber-500 text-foreground text-center text-xs sm:text-sm font-semibold py-2.5 px-4 border-b-2 border-amber-600">
          <span className="inline-flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {T.preview_note}
          </span>
        </div>
      )}
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-5 flex items-center justify-between gap-3">
          <BrandMark size={32} />
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            <a href={`${BACKEND_URL}/api/public/proposal/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="download-pdf-btn"
              className="hidden sm:inline-flex items-center gap-1.5 bg-background hover:bg-[hsl(var(--info-bg))] border border-border text-foreground rounded-md px-3 py-1.5 text-xs">
              <Download className="w-3.5 h-3.5" /> {T.download_pdf}
            </a>
            {/* Mobile-only PDF button (icon only) */}
            <a href={`${BACKEND_URL}/api/public/proposal/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="download-pdf-btn-mobile"
              className="sm:hidden inline-flex items-center justify-center bg-background border border-border text-foreground rounded-full w-9 h-9"
              aria-label={T.download_pdf}>
              <Download className="w-4 h-4" />
            </a>
            {d.care_plan_available && (
              <>
                <a href={`/care-plan/${token}`} target="_blank" rel="noreferrer" data-testid="header-careplan"
                  className="hidden sm:inline-flex items-center gap-1.5 bg-[hsl(var(--info-bg))] hover:bg-[#B8DFF7] border border-primary/20 text-primary rounded-md px-3 py-1.5 text-xs font-semibold">
                  <FileText className="w-3.5 h-3.5" /> Plano Preliminar
                </a>
                <a href={`/care-plan/${token}`} target="_blank" rel="noreferrer" data-testid="header-careplan-mobile"
                  className="sm:hidden inline-flex items-center justify-center bg-[hsl(var(--info-bg))] border border-primary/20 text-primary rounded-full w-9 h-9"
                  aria-label="Plano Preliminar de Cuidados">
                  <FileText className="w-4 h-4" />
                </a>
              </>
            )}
            {!alreadyDeclined && (
              <a href="#aceitacao" data-testid="header-accept" className="inline-flex bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-white font-semibold rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-[11px] sm:text-sm whitespace-nowrap">
                {T.accept_proposal}
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Banner de dados de pagamento — visível assim que a proposta é
          aceite. Mostra a referência única + IBAN + SWIFT + titular.
          O cliente deve incluir esta referência na transferência
          bancária para que o DAF reconcilie automaticamente o pagamento
          da caução (kickstart, remainder ou total do pontual). */}
      {accepted && paymentInfo && paymentInfo.reference && (
        <section className="bg-[hsl(var(--success-bg))] border-b border-[hsl(var(--success))]/25" data-testid="payment-info-banner">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[hsl(var(--success))] font-semibold text-base sm:text-lg mb-1">{T.accepted_thanks}</div>
                <div className="text-[13px] text-[hsl(var(--success))] mb-4">Para avançarmos, efectue a transferência bancária com <b>a referência única abaixo</b> — o nosso departamento financeiro reconcilia automaticamente o pagamento.</div>
                <div className="bg-card rounded-xl border border-[hsl(var(--success))]/25 p-4 sm:p-5 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 sm:gap-3 items-start">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold pt-0.5">Referência</div>
                    <div className="font-mono text-base sm:text-lg font-bold text-[hsl(var(--success))] break-all" data-testid="payment-reference">{paymentInfo.reference}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 sm:gap-3 items-start">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold pt-0.5">Titular</div>
                    <div className="text-sm text-foreground">{paymentInfo.holder}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 sm:gap-3 items-start">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold pt-0.5">IBAN</div>
                    <div className="font-mono text-sm text-foreground break-all" data-testid="payment-iban">{paymentInfo.iban}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 sm:gap-3 items-start">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold pt-0.5">SWIFT</div>
                    <div className="font-mono text-sm text-foreground" data-testid="payment-swift">{paymentInfo.swift}</div>
                  </div>
                  <button
                    onClick={() => {
                      const txt = `Referência: ${paymentInfo.reference}\nTitular: ${paymentInfo.holder}\nIBAN: ${paymentInfo.iban}\nSWIFT: ${paymentInfo.swift}`;
                      navigator.clipboard.writeText(txt);
                      toast.success("Copiado para a área de transferência");
                    }}
                    data-testid="btn-copy-payment"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 py-2 font-semibold"
                  >
                    Copiar dados
                  </button>
                </div>
                <div className="text-[12px] text-[hsl(var(--success))]/80 mt-3">
                  💡 Se pagar por <b>ATM</b> e não conseguir colocar a referência, o nosso DAF confirma manualmente após verificar o comprovativo.
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <main data-testid="proposal-blocks-container">
        {(() => {
          const p = d.proposal || {};
          const isPontual = p.continuity_type === "pontual" ||
            (p.options || []).some(o => (o.mode || "continuidade") === "pontual");
          if (!isPontual || (!p.service_start_date && !p.service_end_date)) return null;
          const fmtDate = (iso) => {
            if (!iso) return "—";
            try { return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" }); }
            catch { return iso; }
          };
          let dayCount = null;
          if (p.service_start_date && p.service_end_date) {
            const s = new Date(p.service_start_date), e = new Date(p.service_end_date);
            if (!isNaN(s) && !isNaN(e) && e >= s) dayCount = Math.round((e - s) / 86400000) + 1;
          }
          return (
            <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 pb-2" data-testid="proposal-service-window">
              <div className="rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-4 sm:p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-primary font-semibold mb-3">Período do serviço</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Início</div>
                    <div className="font-ui text-lg text-primary mt-1" data-testid="proposal-service-start">{fmtDate(p.service_start_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Fim</div>
                    <div className="font-ui text-lg text-primary mt-1" data-testid="proposal-service-end">{fmtDate(p.service_end_date)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Total de dias</div>
                    <div className="font-ui text-lg text-primary mt-1" data-testid="proposal-service-days">{dayCount ? `${dayCount} dia(s)` : "—"}</div>
                  </div>
                </div>
              </div>
            </section>
          );
        })()}
        {blocks.map((b) => (
          <BlockRenderer key={b.id} block={b} data={d} forms={forms} />
        ))}

        {/* 28/02/2026 · Fase 2 — Plano Preliminar acompanha a Proposta.
            Secção dedicada com dois CTAs (Consultar Plano + Descarregar
            PDF) apenas quando o Plano de Cuidados existe. Não usa iframe
            para não afogar a página; o cliente abre em nova aba. */}
        {d.care_plan_available && (
          <section id="plano-preliminar"
                   className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-14"
                   data-testid="proposal-preliminar-section">
            <div className="rounded-3xl border border-[hsl(var(--info))]/25 bg-[hsl(var(--info-bg))]/40 px-5 sm:px-8 py-7 sm:py-10">
              <div className="text-[10px] uppercase tracking-[0.28em] text-primary/70 font-semibold mb-2">
                O SEU PLANO PRELIMINAR DE CUIDADOS
              </div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight text-primary mb-3">
                Preparámos uma proposta de acompanhamento com base nas
                necessidades, rotinas e prioridades identificadas durante
                a Qualificação Técnica.
              </h2>
              <p className="text-sm sm:text-base text-primary/80 leading-relaxed max-w-2xl">
                O Plano Preliminar é o nosso ponto de partida. Após a
                aceitação da proposta e o pagamento integral da caução,
                a Direção Técnica acompanhará a integração inicial e
                validará o plano em contexto domiciliário — só então
                passará a <b>Plano Individual de Cuidados (PIC)</b> definitivo.
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                <a href={`/care-plan/${token}`} target="_blank" rel="noreferrer"
                   data-testid="proposal-preliminar-consultar"
                   className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-primary/90">
                  <FileText className="w-4 h-4" />
                  Consultar plano
                </a>
                <a href={`${BACKEND_URL}/api/public/care-plan/${token}/pdf`} target="_blank" rel="noreferrer"
                   data-testid="proposal-preliminar-pdf"
                   className="inline-flex items-center gap-2 bg-background border border-primary/30 text-primary rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-primary/5">
                  <Download className="w-4 h-4" />
                  Descarregar PDF
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Discrete decline/postpone entry — hidden after acceptance */}
        {!accepted && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16" data-testid="decline-section">
            {alreadyDeclined ? (
              <div className="bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 rounded-2xl p-4 sm:p-6 flex items-start gap-3" data-testid="decline-registered">
                <XCircle className="w-6 h-6 text-[hsl(var(--danger))] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-ui text-lg text-primary mb-1">Registámos a sua recusa.</div>
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    Recebemos a sua decisão em {new Date(d.decline.submitted_at).toLocaleString("pt-PT")}. A Gestora foi notificada e entrará em contacto para percebermos como podemos servi-lo melhor.
                  </div>
                  {d.decline.reason && (
                    <blockquote className="mt-3 border-l-2 border-[hsl(var(--danger))]/40 pl-3 text-sm text-foreground italic">
                      "{d.decline.reason}"
                    </blockquote>
                  )}
                </div>
              </div>
            ) : alreadyPostponed ? (
              <div className="bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-2xl p-4 sm:p-6 flex items-start gap-3" data-testid="postpone-registered">
                <Clock className="w-6 h-6 text-[hsl(var(--warning))] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-ui text-lg text-primary mb-1">Precisa de mais tempo — sem problema.</div>
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    Registámos o pedido em {new Date(d.decline.submitted_at).toLocaleString("pt-PT")}. A Gestora contactá-lo-á brevemente para agendarmos novo momento.
                  </div>
                  {d.decline.reason && (
                    <blockquote className="mt-3 border-l-2 border-[hsl(var(--warning))] pl-3 text-sm text-foreground italic">
                      "{d.decline.reason}"
                    </blockquote>
                  )}
                  <button onClick={() => setShowDecline(true)}
                    className="mt-3 text-xs text-primary hover:text-[hsl(var(--warning))] underline"
                    data-testid="decline-update-btn">
                    Atualizar decisão
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-xs text-muted-foreground mb-3">Ainda precisa de tempo para decidir ou não vai avançar?</div>
                <button onClick={() => setShowDecline(true)}
                  className="text-xs text-muted-foreground hover:text-primary underline underline-offset-4 decoration-dotted"
                  data-testid="open-decline-btn">
                  Recusar ou adiar decisão
                </button>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Decline / postpone modal */}
      {showDecline && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !decliningLoad && setShowDecline(false)}>
          <div className="bg-card rounded-2xl p-5 sm:p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="decline-dialog">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-1">Sua decisão</div>
                <h2 className="font-display text-2xl text-primary">Recusar ou adiar decisão</h2>
              </div>
              <button onClick={() => setShowDecline(false)} disabled={decliningLoad}
                className="p-1 rounded-lg text-muted-foreground hover:bg-background" data-testid="decline-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Diga-nos o que se passa. Ajuda-nos a servi-lo melhor.
            </p>
            <div className="space-y-3 mb-4">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-background transition-colors has-[:checked]:border-[hsl(var(--warning))] has-[:checked]:bg-[hsl(var(--warning))]/5">
                <input type="radio" name="decline_dec" value="postpone"
                  checked={declineDecision === "postpone"} onChange={() => setDeclineDecision("postpone")}
                  className="mt-1" data-testid="decline-radio-postpone" />
                <div>
                  <div className="text-sm font-semibold text-primary inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Preciso de mais tempo</div>
                  <div className="text-xs text-muted-foreground mt-1">A Gestora contactá-lo-á para reagendarmos.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-background transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-[hsl(var(--danger-bg))]">
                <input type="radio" name="decline_dec" value="declined"
                  checked={declineDecision === "declined"} onChange={() => setDeclineDecision("declined")}
                  className="mt-1" data-testid="decline-radio-declined" />
                <div>
                  <div className="text-sm font-semibold text-primary inline-flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-[hsl(var(--danger))]" /> Não vou avançar</div>
                  <div className="text-xs text-muted-foreground mt-1">Recusar definitivamente esta proposta.</div>
                </div>
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">
                  Motivo {declineDecision === "declined" && <span className="text-red-500">*</span>}
                </label>
                <textarea rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder={declineDecision === "declined" ? "Ex: valor acima do orçamento familiar, encontrámos outra solução, etc." : "Ex: preciso de falar com a família, aguardo resposta do banco…"}
                  maxLength={5000}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary resize-y"
                  data-testid="decline-reason" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Seu nome (opcional)</label>
                <input value={declineName} onChange={(e) => setDeclineName(e.target.value)} type="text"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  data-testid="decline-name" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={() => setShowDecline(false)} disabled={decliningLoad}
                className="text-sm px-4 py-2 rounded-lg text-muted-foreground hover:bg-background"
                data-testid="decline-cancel">
                Cancelar
              </button>
              <button onClick={submitDecline} disabled={decliningLoad}
                className={`text-sm px-5 py-2 rounded-lg font-semibold text-white inline-flex items-center gap-2 disabled:opacity-50 ${declineDecision === "declined" ? "bg-red-600 hover:bg-red-700" : "bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]"}`}
                data-testid="decline-submit">
                {decliningLoad ? <Loader2 className="w-4 h-4 animate-spin" /> : (declineDecision === "declined" ? <XCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />)}
                {declineDecision === "declined" ? "Confirmar recusa" : "Enviar pedido"}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="border-t border-border py-8 sm:py-12 text-center px-4">
        <div className="text-xs text-muted-foreground">Precisa de esclarecer algo antes? Fale connosco.</div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 mt-4 text-sm">
          <a href="tel:800450191" className="text-foreground hover:text-primary inline-flex items-center gap-2" aria-label="Ligar para 800 450 191 — chamada grátis">
            <Phone className="w-4 h-4" />
            <span>800 450 191</span>
            <span className="text-[11px] text-muted-foreground/70">· chamada grátis</span>
          </a>
          <a href="mailto:sac.qi@caring.pt" className="text-foreground hover:text-primary flex items-center gap-2"><Mail className="w-4 h-4" /> sac.qi@caring.pt</a>
        </div>
        <div className="mt-8 text-xs text-muted-foreground/60">© Caring · Cuidamos com dignidade</div>
      </footer>
    </div>
  );
}
